/* eslint-disable max-len, implicit-arrow-linebreak */
import * as React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Tooltip,
  Fade,
  CircularProgress,
} from '@mui/material';
import { IoIosArrowDown } from 'react-icons/io';
import { FaEye } from 'react-icons/fa';
import { Job, UpdatedJob } from '../../types';
import JobCard from '../JobCard';

function JobsList(props: any) {
  const {
    loadingHistory,
    appliedJobs,
    jobStatuses,
    jobHistory,
    profileInfo,
    getCandidateJobHistory,
    updateJobStatus,
    updateCandidateComments,
  } = props;

  const appliedJobsCopy: UpdatedJob[] = appliedJobs.map((each: Job) => ({ ...each, expanded: false }));
  const [jobs, setJobs] = React.useState(appliedJobsCopy);
  const [expandedJob, setExpandedJob] = React.useState<number | null>(null);

  const handleAccordionChange = async (job: Job) => {
    const isExpanding = expandedJob !== job.jobOrderId;
    setExpandedJob(isExpanding ? job.jobOrderId : null);

    const updatedJobs = jobs.map((each: UpdatedJob) => ({
      ...each,
      expanded: each.jobOrderId === job.jobOrderId ? isExpanding : false,
    }));

    setJobs(updatedJobs);
    if (isExpanding) {
      await getCandidateJobHistory(job.jobOrderId, true);
    }
  };

  const handleViewJob = (jobId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`/job-requisition/view-job/${jobId}`, '_blank');
  };

  if (!appliedJobs.length) {
    return (
      <div className="w-[80%] self-center mt-6 text-center p-8 bg-white rounded-lg shadow-sm">
        <Typography variant="h6" color="text.secondary">
          No jobs applied yet
        </Typography>
      </div>
    );
  }

  return (
    <div className="w-[80%] self-center mt-6">
      {jobs.map((eachJob: UpdatedJob) => (
        <Accordion
          key={eachJob.jobOrderId}
          expanded={eachJob.expanded}
          onChange={() => handleAccordionChange(eachJob)}
          TransitionProps={{ unmountOnExit: true }}
          sx={{
            mb: 2,
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            },
            transition: 'all 0.3s ease',
            borderRadius: '8px',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              loadingHistory && eachJob.expanded ? (
                <CircularProgress size={20} />
              ) : (
                <IoIosArrowDown className="transition-transform duration-300" />
              )
            }
            aria-controls={`job-${eachJob.jobOrderId}-content`}
            id={`job-${eachJob.jobOrderId}-header`}
            sx={{
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              },
            }}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-4">
                <Typography variant="h6" className="font-medium">
                  {eachJob.jobTitle}
                </Typography>
                <Tooltip title="Job ID" placement="top" arrow TransitionComponent={Fade}>
                  <Typography variant="body2" color="text.secondary" className="cursor-help">
                    ID:
                    {' '}
                    {eachJob.jobId}
                  </Typography>
                </Tooltip>
              </div>
              <div className="flex items-center gap-4">
                {eachJob.jdScore && (
                  <Tooltip title="Match score between your profile and job requirements" placement="top" arrow>
                    <Typography variant="body1">
                      Relevance Score:
                      <span className="font-medium ml-1" style={{ color: `hsl(${eachJob.jdScore}, 100%, 35%)` }}>
                        {eachJob.jdScore}
                        %
                      </span>
                    </Typography>
                  </Tooltip>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FaEye />}
                  onClick={(e) => handleViewJob(eachJob.jobOrderId, e)}
                  sx={{
                    minWidth: '100px',
                    borderRadius: '20px',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                  className="mr-2"
                >
                  View Job
                </Button>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails sx={{ mb: 1, pt: 0 }}>
            <JobCard
              loading={loadingHistory}
              job={eachJob}
              updateJobStatus={updateJobStatus}
              profileInfo={profileInfo}
              jobStatuses={jobStatuses}
              jobHistory={jobHistory && Object.keys(jobHistory).length !== 0 ? jobHistory[eachJob?.jobOrderId] : []}
              getCandidateJobHistory={getCandidateJobHistory}
              updateCandidateComments={updateCandidateComments}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}

export default JobsList;
